import React, { useEffect } from 'react';
import { Box, List, ListItem, ListItemText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Language } from '../../../types/app';
import { ContentStateHandler } from '../../common';
import { ArticleContent } from '@/types/api-definitions';
import {
  fetchArticles,
  selectLocalizedArticles,
  selectArticlesLoading,
  selectArticlesError,
  selectHasArticles,
} from '../../../store/articles';
import { RootState } from '../../../store/types';
import { AppDispatch } from '../../../store';
import { StyledListItemButton, StyledArrowIcon } from './GuidesTab.styles';

const GuidesTab: React.FC = () => {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { slug: apiSlug } = useParams<{ slug: string }>();

  const currentLanguage: Language = i18n.language as Language;
  const localizedArticles = useSelector((state: RootState) =>
    selectLocalizedArticles(state, apiSlug || '', currentLanguage),
  );
  const loading = useSelector((state: RootState) => selectArticlesLoading(state, apiSlug || ''));
  const error = useSelector(selectArticlesError);
  const hasArticles = useSelector((state: RootState) => selectHasArticles(state, apiSlug || ''));

  useEffect(() => {
    if (apiSlug && !hasArticles) {
      dispatch(fetchArticles(apiSlug));
    }
  }, [dispatch, apiSlug, hasArticles]);

  const handleArticleClick = (articleSlug: string) => {
    navigate(`/api-definitions/${apiSlug}/article/${articleSlug}`);
  };

  return (
    <ContentStateHandler
      loading={loading}
      error={error}
      hasContent={localizedArticles.length > 0}
      emptyMessage={t('apiDetail.guidesTab.noArticles')}
    >
      <Box sx={{ p: 0 }}>
        <List>
          {localizedArticles.map((article: ArticleContent) => {
            if (!article) return null;

            return (
              <ListItem key={article.slug} disablePadding>
                <StyledListItemButton onClick={() => handleArticleClick(article.slug)}>
                  <StyledArrowIcon />
                  <ListItemText
                    primary={article.title}
                    slotProps={{ primary: { variant: 'body1' } }}
                  />
                </StyledListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </ContentStateHandler>
  );
};

export default GuidesTab;
